import { makeAutoObservable, runInAction} from 'mobx';
import {
    ProfessionalModel,
    ProfileResponse,
    UpdateProfileResponse,
} from "../../model/professional/professional-model";
import professionalStore from "../../store/professional-store";
import {HTTP_STATUS_CODE} from "../../utils/UHttp";
import toastStore from "../../store/toast-store";
import { setTimeZone } from '../../utils/timezone';
import i18n from '../../i18n/i18n';
import {AuthModel} from "../../model/auth-model";
import Cookies from "js-cookie";
import i18next from "i18next";

export class ProfessionalViewModel {

    errorMessage: string | null = null;
    isLoading: boolean = false;
    isUpdate: boolean = false;

    firstName: string = '';
    lastName: string = '';
    email: string = '';
    phone: string = '';
    profession: string = "";
    timeZone: string = 'Europe/Paris';
    picture: string | null = null;
    isUploadingPicture: boolean = false;
    
    // Onboarding global
    onboarding: boolean = false;
    
    // Onboarding par section
    onboardingStatus: Record<string, boolean> = {
        dashboard: false,
        calendar: false,
        reservation: false,
        //availability: false,
        services: false,
        profile: false
    };

    emailVerified: boolean | undefined = undefined

    currentPassword: string = '';
    newPassword: string = '';
    confirmPassword: string = '';
    language: string = 'fr';

    private professionalModel: ProfessionalModel
    private authModel: AuthModel;

    constructor() {
        makeAutoObservable(this);
        this.professionalModel = ProfessionalModel.getInstance()
        this.authModel = AuthModel.getInstance();
    }

    syncLocalPropertiesFromStore = () => {
        const profile = professionalStore.getProfileProfessional;
        
        const currentUiLanguage = i18n.language;
        
        if (profile) {
            if (profile.language) {
                this.language = profile.language;
                
                if (currentUiLanguage !== profile.language) {
                    this.language = currentUiLanguage;
                    this.handleUpdateLanguage().then();
                }
            } else {
                this.language = currentUiLanguage || 'fr';
            }
            
            this.firstName = profile.firstName || '';
            this.lastName = profile.lastName || '';
            this.email = profile.email || '';
            this.phone = profile.phone || '';
            this.profession = profile.profession || '';
            this.timeZone = profile.timeZone || 'Europe/Paris';
            this.picture = profile.picture || null;
            this.emailVerified = profile.emailVerified;
            this.onboarding = profile.onboarding || false;
            
            setTimeZone(this.timeZone);
        } else {
            this.language = currentUiLanguage || 'fr';
        }
    }

    get getIsUpdate() {
        return this.isUpdate
    }

    setIsUpdate() {
        this.isUpdate = !this.isUpdate
    }

    setFirstName(firstName: string): void{
        this.firstName = firstName
    }

    setLastName(lastName: string): void{
        this.lastName = lastName
    }

    setEmail(email: string): void{
        this.email = email
    }

    setPhone(phone: string): void{
        this.phone = phone
    }

    setProfession(profession: string): void{
        this.profession = profession
    }
    
    setTimeZone(timeZone: string): void{
        this.timeZone = timeZone;
        setTimeZone(timeZone);
    }

    setLanguage(language: string): void{
        this.language = language
    }
    
    handleUpdateLanguage = async (): Promise<void> => {
        try {
            const credentials = {
                language: this.language
            }
            const response = await this.professionalModel.handleUpdateProfil(credentials)
            if(response.success && response.status === HTTP_STATUS_CODE.OK){
                if (professionalStore.getProfileProfessional) {
                    professionalStore.setProfileProfessional({
                        ...professionalStore.getProfileProfessional,
                        language: this.language
                    })
                }
            }
        } catch (error) {
            console.error("error ProfessionalViewModel.handleUpdateLanguage", error)
        }
    }

    setCurrentPassword(currentPassword: string): void{
        this.currentPassword = currentPassword
    }

    setNewPassword(newPassword: string): void{
        this.newPassword = newPassword
    }

    setConfirmPassword(confirmPassword: string): void{
        this.confirmPassword = confirmPassword
    }
    
    handleGetProfil = async (): Promise<void> => {
        try {
            const response: ProfileResponse = await this.professionalModel.handleGetProfil()
            
            if (response.success && response.profile && response.status === HTTP_STATUS_CODE.OK) {
                professionalStore.setProfileProfessional({
                    id: response.profile.id,
                    firstName: response.profile.firstName,
                    lastName: response.profile.lastName,
                    email: response.profile.email,
                    phone: response.profile.phone,
                    profession: response.profile.profession,
                    timeZone: response.profile.timeZone,
                    language: response.profile.language,
                    emailVerified: response.profile.emailVerified,
                    onboarding: response.profile.onboarding,
                    picture: response.profile.picture,
                    stripeConnectedAccountId: response.profile.stripeConnectedAccountId,
                    stripeAccountStatus: response.profile.stripeAccountStatus,
                    absorbStripeFees: response.profile.absorbStripeFees
                })

                this.syncLocalPropertiesFromStore();
            } else if (!response.success) {
                toastStore.error(response.message || 'Une erreur est survenue lors de la récupération du profil');
            }
        } catch (error) {
            console.error("error ProfessionalViewModel.handleGetProfil", error)
            toastStore.error('Une erreur est survenue lors de la récupération du profil');
        }
    }

    handleUpdateProfileProfessional = async (e: any): Promise<void> => {
        e.preventDefault()
        this.isLoading = true;
        try {
            const credentials = {
                id: professionalStore.profileProfessional?.id,
                firstName: this.firstName,
                lastName: this.lastName,
                phone: this.phone,
                profession: this.profession,
                timeZone: this.timeZone,
                language: this.language,
                onboarding: this.onboarding
            }
            const response: UpdateProfileResponse = await this.professionalModel.handleUpdateProfil(credentials)
            if(response.success && response.status === HTTP_STATUS_CODE.OK){
                professionalStore.setProfileProfessional(credentials)
                toastStore.success(response.message);
                this.isLoading = false;
                this.setIsUpdate()
            } else {
                toastStore.error(response.message);
            }
        } catch (error) {
            console.error("error ProfessionalViewModel.handleUpdateProfileProfessional", error)
            toastStore.error('Une erreur est survenue lors de la mise à jour de vos informations');
        }
    }

    handleUpdateSecuritySetting = async (): Promise<void> => {
        this.isLoading = true;
        try {
            const credentials = {
                currentPassword: this.currentPassword,
                newPassword: this.newPassword,
                confirmPassword: this.confirmPassword
            }

            const response: UpdateProfileResponse = await this.professionalModel.handleUpdateSecurity(credentials)
            if(response.success && response.status === HTTP_STATUS_CODE.OK){
                toastStore.success(response.message);
                this.isLoading = false;
                this.setIsUpdate()
                this.setCurrentPassword("")
                this.setNewPassword("")
                this.setConfirmPassword("")
                Cookies.remove("MODESSID");
                Cookies.remove("MODESSID_REFRESH");
                const currentLang = i18next.language;
                setTimeout(() => {window.location.href = `/${currentLang}`;}, 2000)

            } else {
                toastStore.error(response.message);
            }
        } catch (error: any) {
            console.error("error ProfessionalViewModel.handleUpdateProfileBusiness", error)
            toastStore.error('Une erreur est survenue lors de la mise à jour');
        }
    }

    /**
     * Upload la photo de profil du collaborateur
     * @param file Fichier image à uploader
     */
    uploadProfilePicture = async (file: File): Promise<void> => {
        runInAction(() => {
            this.isUploadingPicture = true;
        });

        try {
            const response = await this.professionalModel.uploadProfilePicture(file);

            runInAction(() => {
                if (response.success && response.status === HTTP_STATUS_CODE.OK) {
                    if (response.url) {
                        this.picture = response.url;
                        professionalStore.setProfessionalPicture(response.url);
                    }
                    toastStore.success(response.message);
                } else {
                    toastStore.error(response.message);
                }
                this.isUploadingPicture = false;
            });
        } catch (error: any) {
            console.error("error ProfessionalViewModel.uploadProfilePicture", error);
            runInAction(() => {
                toastStore.error('Une erreur est survenue lors de l\'upload de la photo');
                this.isUploadingPicture = false;
            });
        }
    }

    /**
     * Met à jour le paramètre absorbStripeFees de l'Owner
     * @param absorbStripeFees Nouvelle valeur du paramètre
     */
    updateAbsorbStripeFees = async (absorbStripeFees: boolean): Promise<void> => {
        try {
            const response = await this.professionalModel.updateAbsorbStripeFees(absorbStripeFees);

            if (response.success && response.status === HTTP_STATUS_CODE.OK) {
                // Mettre à jour le store
                if (professionalStore.getProfileProfessional) {
                    professionalStore.setProfileProfessional({
                        ...professionalStore.getProfileProfessional,
                        absorbStripeFees
                    });
                }
                toastStore.success(response.message);
            } else {
                toastStore.error(response.message);
            }
        } catch (error: any) {
            console.error("error ProfessionalViewModel.updateAbsorbStripeFees", error);
            toastStore.error('Une erreur est survenue lors de la mise à jour');
        }
    }

    /**
     * Supprime le compte de l'utilisateur et toutes ses données associées
     * - Pour un owner: supprime toutes les companies, abonnements, Stripe Connect, etc.
     * - Pour un collaborateur: supprime uniquement son profil
     */
    handleDeleteAccount = async (): Promise<void> => {
        this.isLoading = true;
        try {
            const response = await this.professionalModel.deleteAccount();

            if (response.success && response.status === HTTP_STATUS_CODE.OK) {
                toastStore.success(response.message);

                // Supprimer les cookies d'authentification
                Cookies.remove("MODESSID");
                Cookies.remove("MODESSID_REFRESH");

                // Nettoyer le store
                professionalStore.setProfileProfessional(null);

                // Rediriger vers la page de login après 2 secondes
                const currentLang = i18next.language;
                setTimeout(() => {
                    window.location.href = `/${currentLang}/login`;
                }, 2000);
            } else {
                toastStore.error(response.message);
                this.isLoading = false;
            }
        } catch (error: any) {
            console.error("error ProfessionalViewModel.handleDeleteAccount", error);
            toastStore.error('Une erreur est survenue lors de la suppression du compte');
            this.isLoading = false;
        }
    }
}

const professionalViewModel = new ProfessionalViewModel()

export const useProfessionalViewModel = () => {
    return  professionalViewModel
}